/**
 * The two Pay What You Want validation rules, in order.
 * Pure TypeScript - no React imports.
 *
 * Rule A — a row's Minimum Price must be at least the Platform Minimum.
 * Rule B — a row's Suggested Price must be strictly greater than its Minimum Price.
 *
 * Rule B is suppressed while Rule A fails on the same row, because it would otherwise be
 * judging a value the coach is about to change. That is why a row reports at most one
 * error.
 *
 * These rules judge the amounts a coach has actually entered. Requiredness is a form
 * concern and stays in the Yup schemas.
 */

import {findPlatformMinimum} from "./platform-minimum";
import type {PricingCurrencyRow} from "./pricing.types";

export const MINIMUM_BELOW_PLATFORM_MINIMUM = "minimum-below-platform-minimum";
export const SUGGESTED_NOT_ABOVE_MINIMUM = "suggested-not-above-minimum";

export type PwywRuleName =
  | typeof MINIMUM_BELOW_PLATFORM_MINIMUM
  | typeof SUGGESTED_NOT_ABOVE_MINIMUM;

export interface PwywRowError {
  /** The field the error belongs against, so it renders on the right input. */
  field: "minimum" | "suggested";
  rule: PwywRuleName;
  message: string;
}

/**
 * Validates one currency row under Pay What You Want.
 *
 * @param row - the row's currency, Minimum Price and Suggested Price
 * @returns the single error to show, or `null` when the row is acceptable
 */
export function validatePwywRow(row: PricingCurrencyRow): PwywRowError | null {
  const {minimum, suggested} = row;
  const platformMinimum = findPlatformMinimum(row.currency);

  if (isEnteredAmount(minimum) && platformMinimum !== null && minimum < platformMinimum) {
    return {
      field: "minimum",
      rule: MINIMUM_BELOW_PLATFORM_MINIMUM,
      message: `You must set at least ${row.currency} ${platformMinimum} as a minimum price`,
    };
  }

  if (isEnteredAmount(minimum) && isEnteredAmount(suggested) && suggested <= minimum) {
    return {
      field: "suggested",
      rule: SUGGESTED_NOT_ABOVE_MINIMUM,
      message: "Your suggested price must be higher than your minimum price",
    };
  }

  return null;
}

/**
 * Validates every currency row independently.
 *
 * @param rows - the offering's currency rows
 * @returns one entry per row, positionally, each the row's error or `null`
 */
export function validatePwywRows(rows: PricingCurrencyRow[]): (PwywRowError | null)[] {
  return rows.map(validatePwywRow);
}

function isEnteredAmount(amount: number | undefined): amount is number {
  return typeof amount === "number" && Number.isFinite(amount);
}
